import React, {useEffect, useRef, useState} from 'react';
import {Formik} from 'formik';
import * as Yup from 'yup';
import {upsertLoadCell} from 'api/DevicesApi';
import {craneCommunicationTypes} from 'constants/dataConstants';
import {hexRegex, ipRegex} from 'constants/uiConstants';
import {useAppContext} from 'contexts/AppContext';
import {isSystemAdmin} from 'utils/Auth';
import {isEmpty, isSwitchOn} from 'utils/Utils';
import LoadCellForm from './LoadCellForm';

const DEFAULT_INSTANCE = {
    id: '',
    name: '',
    motion_id: '',
    manufacturer: '',
    lc_class: '1',
    part_no: '',
    serial_no: '',
    unit: '',
    output: '',
    crane_communication_type: '',
    mb_address: '',
    mb_baud_rate: '',
    mb_parity: '',
    modbus_register: '',
    mode: '',
    calibration_a: '',
    calibration_b: '',
    calibration_c: '',
    active: ['1'],
    ts: 0,
};
const VALIDATION_SCHEMA = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    motion_id: Yup.string().required('Motion is required'),
    unit: Yup.string().required('Unit is required'),
    output: Yup.string().required('Output is required'),
    mb_address: Yup.string()
        .when(['output'], {
            is: output => Number(output) === 2,
            then: schema => schema.required('Modbus address is required')
        })
        .when(['crane_communication_type'], {
            is: crane_communication_type => crane_communication_type === 'MODBUSRTU',
            then: schema => schema.matches(hexRegex, 'Address is not valid')
        })
        .when(['crane_communication_type'], {
            is: crane_communication_type => crane_communication_type === 'PLC',
            then: schema => schema.matches(ipRegex, 'Address is not valid')
        }),
    mb_baud_rate: Yup.number().when(['output', 'crane_communication_type'], {
        is: (output, crane_communication_type) => Number(output) === 2 && crane_communication_type === 'MODBUSRTU',
        then: schema => schema.required('Baud rate is required')
    }),
    mb_parity: Yup.string().when(['output', 'crane_communication_type'], {
        is: (output, crane_communication_type) => Number(output) === 2 && crane_communication_type === 'MODBUSRTU',
        then: schema => schema.required('Parity is required')
    }),
    modbus_register: Yup.string().when(['output'], {
        is: output => Number(output) === 1,
        then: schema => schema.required('Modbus register is required')
    }),
    mode: Yup.string().when(['output'], {
        is: output => Number(output) === 1,
        then: schema => schema.required('Mode is required')
    }),
    calibration_a: Yup.number().when(['output', 'mode'], {
        is: (output, mode) => Number(output) === 1 && Number(mode),
        then: schema => schema.required('Factor A is required')
    }),
    calibration_b: Yup.number().when(['output', 'mode'], {
        is: (output, mode) => Number(output) === 1 && Number(mode),
        then: schema => schema.required('Factor B is required')
    }),
    calibration_c: Yup.number().when(['output', 'mode'], {
        is: (output, mode) => Number(output) === 1 && Number(mode) === 2,
        then: schema => schema.required('Factor C is required')
    }),
});

export default function LoadCellDetails({
    pageState, currentVersion, currentConfiguration, onDataUpdate, motions, loadCell, onCloseButtonClick
}) {
    const isMounted = useRef(false);
    const {showMessage, handleApiError} = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [initialValues, setInitialValues] = useState({...DEFAULT_INSTANCE});
    const isEditable = isSystemAdmin() && currentVersion.status === 0;

    useEffect(() => {
        isMounted.current = true;
        getInitialValues();
        return () => isMounted.current = false;
    }, [loadCell, pageState, currentVersion, currentConfiguration]);

    const getInitialValues = () => {
        if (isMounted.current) {
            setIsLoading(true);
            if (!isEmpty(loadCell)) {
                setInitialValues(oldData => {
                    const newData = {};
                    for (const [key, value] of Object.entries(oldData)) {
                        if (key.startsWith('calibration_')) {
                            let dataKey = key.substring(12);
                            if (loadCell.calibration && loadCell.calibration.hasOwnProperty(dataKey)) newData[key] = loadCell.calibration[dataKey];
                            else newData[key] = value;
                        }
                        else if (key.startsWith('mb_')) {
                            let dataKey = key.substring(3);
                            if (loadCell.modbus_connection && loadCell.modbus_connection.hasOwnProperty(dataKey)) newData[key] = loadCell.modbus_connection[dataKey];
                            else newData[key] = value;
                        }
                        else if (loadCell.hasOwnProperty(key)) newData[key] = loadCell[key];
                        else newData[key] = value;
                    }
                    newData.crane_communication_type = craneCommunicationTypes[currentConfiguration.crane_details.crane_comm_type];
                    newData.active = newData.active ? ['1'] : [];
                    newData.modbus_register = newData.modbus_register || '';
                    newData.mode = newData.mode || '';
                    newData.ts = Date.now();
                    return newData;
                });
            }
            else {
                setInitialValues({
                    ...DEFAULT_INSTANCE,
                    crane_communication_type: !isEmpty(currentConfiguration) ? craneCommunicationTypes[currentConfiguration.crane_details.crane_comm_type] : '',
                    ts: Date.now(),
                });
            }
            setIsLoading(false);
        }
    };

    const handleSubmit = (values, {setSubmitting, setErrors}) => {
        setIsLoading(true);
        const payload = {
            id: values.id,
            name: values.name,
            motion_id: Number(values.motion_id),
            manufacturer: values.manufacturer,
            lc_class: Number(values.lc_class),
            part_no: values.part_no,
            serial_no: values.serial_no,
            output: Number(values.output),
            unit: values.unit,
            active: isSwitchOn(values.active),
        };
        if (payload.output === 1) {  // analog
            payload.modbus_register = Number(values.modbus_register);
            payload.mode = Number(values.mode);
            payload.calibration = {a: Number(values.calibration_a), b: Number(values.calibration_b)};
            if (payload.mode === 2) payload.calibration.c = Number(values.calibration_c);
        }
        else if (payload.output === 2) {  // digital
            payload.modbus_connection = {address: values.mb_address};
            if (currentConfiguration.crane_details.crane_comm_type === 1) {
                payload.modbus_connection.baud_rate = Number(values.mb_baud_rate);
                payload.modbus_connection.parity = Number(values.mb_parity);
            }
        }
        const onError = error => handleFailureResponse(error, setErrors);
        const onDone = () => {
            if (isMounted.current) {
                setSubmitting(false);
                setIsLoading(false);
            }
        };
        upsertLoadCell(pageState.craneId, payload, handleSuccessResponse, onError, onDone);
    };

    const handleSuccessResponse = (response) => {
        if (isMounted.current) {
            showMessage('success', 'Load cell configuration has been successfully saved');
            onDataUpdate();
            onCloseButtonClick();
        }
    };

    const handleFailureResponse = (error, setErrors) => {
        handleApiError(error);
        if (isMounted.current && error.response) {
            setErrors(error.response.data.errors);
        }
    };

    return (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={VALIDATION_SCHEMA}
          onSubmit={handleSubmit}
        >
            {(props) => (
                <LoadCellForm
                  {...props}
                  motions={motions}
                  isEditable={isEditable}
                  isLoading={isLoading}
                  onCloseButtonClick={onCloseButtonClick}
                />
            )}
        </Formik>
    );
}
